import { useApolloClient, useQuery } from '@apollo/client';
import { useNavigation, useRoute } from '@react-navigation/native';
import * as Sentry from '@sentry/core';
import { useEffect, useLayoutEffect, useState } from 'react';

import { ConfirmationModal } from '@oui/app-core/src/components/ConfirmationModal';
import { HeaderButtons, HeaderItem } from '@oui/app-core/src/components/HeaderButtons';
import { Text } from '@oui/app-core/src/components/Text';
import { View } from '@oui/app-core/src/components/View';
import { Theme, useTheme } from '@oui/app-core/src/styles';
import { NumberParam } from '@oui/app-core/src/types/navigation';
import { graphql, ResultOf } from '@oui/lib/src/graphql/tada';

import { DistressRatingForm } from '../../components/DistressRatingForm/DistressRatingForm';
import { RatingAfterForm } from '../../components/RatingAfterForm/RatingAfterForm';
import { StressBreathingBreak } from '../../components/StressBreathingBreak/StressBreathingBreak';
import { StressBreathingRound } from '../../components/StressBreathingRound/StressBreathingRound';
import { StackScreenProps } from '../../types/navigation';

export const StressBreathingNewQuery = graphql(`
  query StressBreathingNew {
    user {
      ID
      role {
        ID
      }
    }
  }
`);

export const StressBreathingNewBreathHoldingMutation = graphql(`
  mutation StressBreathingNewBreathHolding($input: MutationAddBreathHoldingPracticeInput!) {
    addBreathHoldingPractice(input: $input) {
      practiceID
    }
  }
`);

export const StressBreathingNewHyperventilationMutation = graphql(`
  mutation StressBreathingNewHyperventilationHolding(
    $input: MutationAddHyperventilationPracticeInput!
  ) {
    addHyperventilationPractice(input: $input) {
      practiceID
    }
  }
`);

export const StressBreathingNewBreathHoldingUpdateMutation = graphql(`
  mutation StressBreathingNewBreathHoldingUpdate(
    $input: MutationUpdateBreathHoldingPracticeInput!
  ) {
    updateBreathHoldingPractice(input: $input) {
      practiceID
    }
  }
`);

export const StressBreathingNewHyperventilationUpdateMutation = graphql(`
  mutation StressBreathingNewHyperventilationHoldingUpdate(
    $input: MutationUpdateHyperventilationPracticeInput!
  ) {
    updateHyperventilationPractice(input: $input) {
      practiceID
    }
  }
`);

function getBackgroundColor(theme: Theme, step: 'finish' | 'break' | 'distress' | 'round') {
  return {
    break: theme.color.gray100,
    round: theme.color.dark,
    finish: undefined,
    distress: undefined,
  }[step];
}

/**
 * Flow to create / perform a new stress breathing exercise.
 */
export const StressBreathingNew = () => {
  const route = useRoute<StackScreenProps<'StressBreathingNew'>['route']>();
  const navigation = useNavigation<StackScreenProps<'StressBreathingNew'>['navigation']>();
  const { theme } = useTheme();
  const { data } = useQuery(StressBreathingNewQuery, { fetchPolicy: 'cache-first' });
  const [showCancelModal, setShowCancelModal] = useState(false);
  const roundNum = Number(route.params.round);
  const [roundFinished, setRoundFinished] = useState(false);
  const [distressRatings, setDistressRatings] = useState<number[]>([]);
  const apollo = useApolloClient();
  const patientID = data?.user?.role?.ID;

  const step: 'finish' | 'break' | 'distress' | 'round' =
    route.params.break === 'true'
      ? route.params.round === '4'
        ? 'finish'
        : 'break'
      : roundFinished
        ? 'distress'
        : 'round';

  useLayoutEffect(() => {
    navigation.setOptions({
      headerStyle: {
        backgroundColor: getBackgroundColor(theme, step),
        borderWidth: 0,
      },
      headerTitle: '',
      headerLeft: ({}) => (
        <HeaderButtons left>
          <HeaderItem
            aria-label="Cancel exercise"
            title="Cancel exercise"
            iconName="arrow-left"
            color={theme.color.primary100}
            onPress={() => setShowCancelModal(true)}
          />
        </HeaderButtons>
      ),
      headerRight: () => {
        return (
          <HeaderButtons>
            <Text
              text={`${roundNum + 1} of 5`}
              size={15}
              color={step === 'distress' ? undefined : 'white'}
            />
          </HeaderButtons>
        );
      },
    });
  }, [step, roundNum, navigation, theme]);

  useEffect(() => {
    async function syncPractice() {
      const type = route.params.type;
      if (distressRatings.length) {
        if (route.params.practiceID) {
          const mutation =
            type === 'holding'
              ? StressBreathingNewBreathHoldingUpdateMutation
              : StressBreathingNewHyperventilationUpdateMutation;
          await apollo.mutate({
            mutation,
            variables: {
              input: {
                practiceID: route.params.practiceID!,
                practiceValues: { patientID: patientID!, ratings: [] },
                stressBreathingExercise: {
                  rounds: distressRatings.map((distressRating) => ({ distressRating })),
                },
              },
            },
          });
        } else {
          const mutation =
            type === 'holding'
              ? StressBreathingNewBreathHoldingMutation
              : StressBreathingNewHyperventilationMutation;
          const response = await apollo.mutate<ResultOf<typeof mutation>>({
            mutation,
            variables: {
              input: {
                practiceValues: { patientID, ratings: [] },
                stressBreathingExercise: {
                  rounds: distressRatings.map((distressRating) => ({ distressRating })),
                },
              },
            },
          });

          const practiceID =
            response.data && 'addBreathHoldingPractice' in response.data
              ? response.data.addBreathHoldingPractice.practiceID
              : response.data?.addHyperventilationPractice.practiceID;
          navigation.setParams({ practiceID });
        }
      }
    }
    void syncPractice();
  }, [apollo, route.params.practiceID, route.params.type, navigation, distressRatings, patientID]);

  if (!patientID) return null;

  return (
    <View testID="StressBreathingNew" style={{ flex: 1 }}>
      {step === 'finish' ? null : (
        <View style={{ width: '100%', height: 5, backgroundColor: theme.color.accent300 }}>
          <View
            style={{
              width: `${((roundNum + 1) / 5) * 100}%`,
              height: 5,
              backgroundColor: theme.color.accent100,
            }}
          />
        </View>
      )}
      <View style={{ padding: 20, backgroundColor: getBackgroundColor(theme, step), flex: 1 }}>
        {step === 'finish' ? (
          <RatingAfterForm
            onSubmit={(value) => {
              const mutation =
                route.params.type === 'holding'
                  ? StressBreathingNewBreathHoldingUpdateMutation
                  : StressBreathingNewHyperventilationUpdateMutation;
              apollo
                .mutate({
                  mutation,
                  variables: {
                    input: {
                      practiceID: route.params.practiceID!,
                      practiceValues: { patientID, ratings: [{ type: 'RATING_AFTER', value }] },
                      stressBreathingExercise: {
                        rounds: distressRatings.map((distressRating) => ({ distressRating })),
                      },
                    },
                  },
                })
                .then(() => {
                  navigation.replace('StressBreathingShow', {
                    type: route.params.type,
                    practiceID: route.params.practiceID!,
                  });
                })
                .catch(Sentry.captureException);
            }}
          />
        ) : step === 'break' ? (
          <StressBreathingBreak
            round={roundNum}
            onStart={() => {
              setRoundFinished(false);
              navigation.setParams({
                round: (roundNum + 1).toString() as NumberParam,
                break: 'false',
              });
            }}
          />
        ) : step === 'distress' ? (
          <DistressRatingForm
            onSubmit={(rating) => {
              setDistressRatings((ratings) => [...ratings, rating]);
              navigation.setParams({ break: 'true' });
            }}
          />
        ) : (
          <StressBreathingRound
            pause={showCancelModal}
            round={roundNum}
            type={route.params.type}
            onFinish={() => setRoundFinished(true)}
          />
        )}
      </View>
      <ConfirmationModal
        visible={showCancelModal}
        title="Leave before finishing?"
        description="You are about the leave this exercise before completing all 5 rounds. Once you exit, you’ll have to start over."
        cancelText="Yes, leave"
        confirmText="Let's finish"
        onCancel={() => navigation.navigate('StressBreathingIndex', { type: route.params.type })}
        onConfirm={() => setShowCancelModal(false)}
      />
    </View>
  );
};
